'use client'

import { Avatar } from '@repo/ui/Avatar'
import { CaseManagementIcon } from '@repo/ui/CaseManagementIcon'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { ClientNameAndMatterDescription } from '@repo/ui/ClientNameAndMatterDescription'
import { EnterIcon } from '@repo/ui/EnterIcon'
import { HeaderSearchIcon } from '@repo/ui/HeaderSearchIcon'
import { Link } from 'react-router-dom'
import { MatterHistoryIcon } from '../../icons/MatterHistoryIcon/MatterHistoryIcon'
import { MatterList } from '../../lockups/MatterList/MatterList'
import { Portal } from '@mui/base/Portal'
import { useClick, useMenuState, ControlledMenu } from '@szhsin/react-menu'
import { useEffect, useState, useRef } from 'react'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { UserIcon } from '@repo/ui/UserIcon'
import { useTheme } from '@mui/material/styles'
import MUIBackdrop from '@mui/material/Backdrop'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/TextField'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './MatterSelector.module.scss'

export const MatterSelector = ({ fetchRecentMatters_DI }) => {
  const theme = useTheme()
  const ref = useRef(null)
  const searchBoxRef = useRef(null)
  const isLoading = useLoadingStore((state) => state.isLoading)
  const [searchTerm, setSearchTerm] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [showRecent, setShowRecent] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const anchorProps = useClick(menuState.state, toggleMenu)
  const { currentClientMatterNo, recentMatters, fetchRecentMatters, matters, fetchMatterSearch, currentMatter } =
    useMattersStore()
  const { clientNo, matterNo, matterDescription, clientName, forename, surname, feeEarnerDescription, feeEarnerId } =
    matters && currentClientMatterNo && matters[currentClientMatterNo] ? matters[currentClientMatterNo] : {}

  const renderClientName = () => {
    if (forename && surname) return `${forename} ${surname}`
    if (surname) return surname
    if (clientName) return clientName
    return ''
  }

  useEffect(() => {
    setIsOpen(menuState?.state?.includes('open'))
  }, [setIsOpen, menuState])

  useEffect(() => {
    if (isOpen) {
      searchBoxRef.current.focus()
    } else {
      setSearchResults(null)
      setSearchTerm('')
      setShowRecent(true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!searchTerm) {
      setShowRecent(true)
      setSearchResults(null)
    }
  }, [searchTerm])

  useEffect(() => {
    fetchRecentMatters_DI ? fetchRecentMatters_DI() : fetchRecentMatters()
  }, [clientNo, matterNo, fetchRecentMatters, fetchRecentMatters_DI])

  useEffect(() => {
    if (searchTerm) {
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived])

  const handleSearch = async () => {
    if (!searchTerm) {
      setSearchResults(null)
      return
    }

    try {
      setShowRecent(false)
      setIsProcessing(true)
      const { matterSearchResults, searchQueriesStillInProgress } = await fetchMatterSearch({
        searchCriteria: searchTerm,
        includeArchived: includeArchived
      })

      if (!matterSearchResults) {
        // this might happen if multiple requests come back out of sequence; we only return a value for the last one, so ignore the others
        return
      }

      setSearchResults(matterSearchResults)
      setIsProcessing(searchQueriesStillInProgress)
    } catch (err) {
      setIsProcessing(false)
      setSearchResults(null)
    }
  }

  return (
    <MUIStack direction="row" justifyContent="flex-start" alignItems="center" gap={3}>
      <MUIStack
        data-testid="MatterSelector"
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        className={`${styles.matterSelector} ${isOpen ? styles.open : ''}`}
        sx={{
          padding: currentClientMatterNo ? '7px 6px 3px 47px' : '5px 6px',
          position: 'relative',
          backgroundColor: 'background.dark',
          '&:hover': {
            backgroundColor: 'background.dark',
            filter: 'brightness(1.02)'
          }
        }}
        ref={ref}
        {...anchorProps}
      >
        <Avatar
          tooltipText={''}
          feeEarnerId={feeEarnerId}
          name={feeEarnerDescription || ''}
          size="36px"
          sx={{ position: 'absolute', top: '3px', left: '3px' }}
        >
          {feeEarnerDescription ? null : <CaseManagementIcon sx={{ color: 'white', width: '18px' }} />}
        </Avatar>

        {currentClientMatterNo && currentMatter ? (
          <>
            <ClientNameAndMatterDescription
              clientNo={clientNo}
              matterNo={Number(matterNo)}
              surname={renderClientName()}
              matterDescription={matterDescription}
              isCurrentMatter={true}
              highlightIfHighRisk={true}
            />
            <ChevronRightIcon className={styles.matterSelectorChevron} sx={{ color: theme.palette.text.secondary }} />
          </>
        ) : (
          <>
            <MUITypography
              variant="body1"
              sx={{
                mb: 0,
                ml: 10,
                pointerEvents: 'none',
                width: '100%',
                paddingLeft: '0px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Select a matter
            </MUITypography>
            <ChevronRightIcon className={styles.matterSelectorChevron} sx={{ color: theme.palette.text.secondary }} />
          </>
        )}
      </MUIStack>

      <ControlledMenu
        {...menuState}
        anchorRef={ref}
        onClose={() => toggleMenu(false)}
        transition={{ close: false }}
        className="matterSelectorMenu"
        portal
      >
        <MUIStack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ height: '54px', padding: '0px 8px', margin: '0px 8px' }}
        >
          <MUIInput
            sx={{
              mt: 6,
              width: '100%',
              input: {
                backgroundColor: 'background.dark',
                height: '40px',
                padding: '0 0 0 14px',
                borderRadius: '6px'
              },
              '.MuiInputBase-root': {
                borderRadius: '6px',
                height: '40px',
                paddingRight: 0,
                backgroundColor: 'background.dark'
              },
              'fieldset, .Mui-focused fieldset': {
                border: 'none'
              }
            }}
            placeholder="Search all matters"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            inputProps={{ ref: searchBoxRef, maxLength: 80 }}
            InputProps={{
              endAdornment: (
                <MUIInputAdornment position="end">
                  <MUIIconButton
                    onClick={handleSearch}
                    disabled={!searchTerm}
                    sx={{
                      borderRadius: '0 6px 6px 0',
                      height: '40px',
                      backgroundColor: searchTerm ? 'background.slate100' : 'transparent',
                      opacity: searchTerm ? 1 : 0,
                      cursor: searchTerm ? 'pointer' : 'not-allowed',
                      transition: 'all 250ms',
                      alignItems: 'center'
                    }}
                  >
                    <MUITypography
                      variant="h6"
                      sx={{
                        mb: 0,
                        mr: 2,
                        pointerEvents: 'none',
                        width: '100%',
                        paddingLeft: '0px',
                        fontSize: '12px',
                        fontWeight: '400',
                        textTransform: 'uppercase'
                      }}
                    >
                      Search
                    </MUITypography>
                    <EnterIcon
                      sx={{
                        height: '24px',
                        width: '24px',
                        color: searchTerm ? 'white' : 'grey',
                        cursor: searchTerm ? 'pointer' : 'not-allowed',
                        transition: 'all 250ms'
                      }}
                    />
                  </MUIIconButton>
                </MUIInputAdornment>
              )
            }}
          />
          <MUIFormControlLabel
            control={<MUICheckbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
            label="Include archived"
          />
        </MUIStack>

        <MUIStack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ width: '700px', height: '100%', marginTop: 16 }}
        >
          <div style={{ width: '440px', height: '100%' }}>
            {
              // only render if isOpen, as that helps the list reset correctly when closed
              isOpen && showRecent ? (
                <>
                  {renderLabel(`Recent matters`)}
                  <MatterList
                    matters={recentMatters}
                    minHeight="420px"
                    maxHeight="420px"
                    isLoading={isLoading}
                    isEmpty={!recentMatters?.length}
                    clickHandler={() => toggleMenu(false)}
                    showLoadingState={false}
                  />
                </>
              ) : null
            }

            {
              // only render if isOpen, as that helps the list reset correctly when closed
              isOpen && !showRecent ? (
                <>
                  <MUIStack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    {renderLabel(`Results for '${searchTerm}'`)}
                    <MUITypography
                      variant="body1"
                      onClick={() => setSearchTerm('')}
                      sx={{
                        mt: 0,
                        mb: 1,
                        mr: 2,
                        cursor: 'pointer',
                        color: 'secondary.main',
                        paddingRight: '16px',
                        fontSize: '12px',
                        fontWeight: '400'
                      }}
                    >
                      Clear search
                    </MUITypography>
                  </MUIStack>
                  <MatterList
                    matters={searchResults}
                    minHeight="420px"
                    maxHeight="420px"
                    isLoading={isLoading || isProcessing}
                    isEmpty={!searchResults?.length}
                    clickHandler={() => toggleMenu(false)}
                  />
                </>
              ) : null
            }
          </div>

          <MUIStack
            sx={{ padding: '0 16px 0 20px', height: '424px', flex: 1, borderLeft: '1px solid #E0E0E0' }}
            direction="column"
            gap={2}
            alignItems="stretch"
            justifyContent="flex-start"
          >
            {renderLabel(`Not seeing what you need?`, 0)}

            {renderSidebarOption({
              title: 'See all matters',
              description: 'View all matters and filter by values like work type, status, and more',
              Icon: CaseManagementIcon,
              toggleMenu,
              theme,
              target: 'case-management/matters'
            })}

            {renderSidebarOption({
              title: 'See all clients',
              description: 'View all clients and filter by values like name, email, and more',
              Icon: UserIcon,
              toggleMenu,
              theme,
              target: 'case-management/clients'
            })}

            {renderSidebarOption({
              title: searchTerm
                ? `Search Matter History for '${searchTerm}'`
                : `Enter a search term to search Matter History`,
              description: 'Find documents, emails and more across all matters',
              Icon: MatterHistoryIcon,
              toggleMenu,
              theme,
              target: `search/search-results/matter-history?isSearch=true&search=${searchTerm}`,
              disabled: !searchTerm
            })}
          </MUIStack>
        </MUIStack>
      </ControlledMenu>

      <Portal>
        <MUIBackdrop
          open={isOpen || false}
          sx={{ backgroundColor: 'rgb(0,0,0,0.3)', zIndex: 900 }}
          onClick={() => toggleMenu(false)}
        />
      </Portal>

      <MUITooltip title={'Search all matters'} placement="right" arrow>
        <MUIIconButton
          sx={{
            width: '36px',
            height: '36px',
            padding: '12px',
            backgroundColor: 'success.light',
            transition: 'background-color 0.3s',
            '&:hover': {
              backgroundColor: 'success.lightHover'
            }
          }}
          onClick={() => toggleMenu(true)}
        >
          <HeaderSearchIcon sx={{ width: '18px', height: '18px', color: 'white' }} />
        </MUIIconButton>
      </MUITooltip>
    </MUIStack>
  )
}

MatterSelector.propTypes = {
  fetchRecentMatters_DI: PropTypes.func
}

const renderLabel = (label, paddingLeft = '16px') => {
  return (
    <MUITypography
      variant="h6"
      sx={{
        mb: 1,
        mt: 0,
        pointerEvents: 'none',
        paddingLeft,
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}
    >
      {label}
    </MUITypography>
  )
}

const renderSidebarOption = ({ title, Icon, description, toggleMenu, theme, target, disabled = false }) => {
  return (
    <Link
      to={target}
      style={{
        textDecorationColor: theme.palette?.secondary?.main || 'blue',
        pointerEvents: disabled ? 'none' : 'auto'
      }}
      onClick={() => (disabled ? null : toggleMenu(false))}
    >
      <MUIButton
        disabled={disabled}
        variant="outlined"
        sx={{
          textAlign: 'left',
          width: '100%',
          textTransform: 'none',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          borderColor: '#E0E0E0',
          opacity: disabled ? 0.7 : 1
        }}
      >
        <Icon sx={{ width: '18px', marginRight: '12px', color: '#273443', cursor: 'pointer' }} />
        <MUIStack direction="column" sx={{ paddingTop: '2px' }}>
          <MUITypography variant="body1" sx={{ cursor: 'pointer', mb: 0.5, wordBreak: 'break-word' }}>
            {title}
          </MUITypography>
          <MUITypography variant="body2" sx={{ opacity: 0.6, cursor: 'pointer' }}>
            {description}
          </MUITypography>
        </MUIStack>
      </MUIButton>
    </Link>
  )
}
